<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSession } from '../auth';
import { useCurriculumDesigner } from '../composables/useCurriculumDesigner';
import AppLayout from '@/components/layout/AppLayout.vue';
import CurriculaNav from '@/components/layout/CurriculaNav.vue';
import { Button } from '@/components/ui/button';
import { marked } from 'marked';

marked.setOptions({ async: false });

// Ensure blank lines before block-level markdown elements.
// LLMs often use single \n between blocks, but markdown requires \n\n.
function normalizeMarkdown(text: string): string {
  return text
    // Blank line before heading lines (# ...)
    .replace(/(?<!\n)\n(#{1,6} )/g, '\n\n$1')
    // Blank line before horizontal rules (---, ***, ___)
    .replace(/(?<!\n)\n(---+|\*\*\*+|___+)\s*$/gm, '\n\n$1')
    // Blank line before the first list item in a run (- item or * item or 1. item)
    .replace(/([^\n])\n([-*] |\d+\. )/g, '$1\n\n$2');
}

const router = useRouter();
const route = useRoute();
const session = useSession();
const user = computed(() => session.value?.data?.user);
const isLoading = computed(() => session.value?.isPending);

const { messages, isStreaming, isLoadingSession, createdCurriculumId, error, sessionId, sendMessage, loadSession } =
  useCurriculumDesigner();

const inputText = ref('');
const messagesContainer = ref<HTMLElement | null>(null);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

function autoResize(event: Event) {
  const el = event.target as HTMLTextAreaElement;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
  scrollToBottom();
}

async function handleSubmit() {
  if (!inputText.value.trim() || isStreaming.value) return;
  const text = inputText.value;
  inputText.value = '';
  if (textareaRef.value) textareaRef.value.style.height = 'auto';
  scrollToBottom();
  await sendMessage(text);
  scrollToBottom();

  // Update URL with sessionId after first message creates the session
  if (sessionId.value && !route.query.sessionId) {
    router.replace({ query: { ...route.query, sessionId: sessionId.value } });
  }
}

// Auto-scroll when messages change
watch(
  () => messages.value.map((m) => m.content).join(''),
  () => scrollToBottom()
);

onMounted(async () => {
  if (!isLoading.value && !user.value) {
    router.push('/sign-in');
    return;
  }

  // Resume existing session if sessionId is in the URL
  const existingSessionId = route.query.sessionId as string | undefined;
  if (existingSessionId) {
    await loadSession(existingSessionId);
    scrollToBottom();
    return;
  }

  // Auto-send the topic from the query param
  const topic = route.query.topic as string | undefined;
  if (topic) {
    await sendMessage(topic);
    if (sessionId.value) {
      router.replace({ query: { sessionId: sessionId.value } });
    }
  }
});
</script>

<template>
  <AppLayout>
    <template #nav>
      <CurriculaNav />
    </template>

    <!-- Loading state -->
    <div
      v-if="isLoadingSession"
      class="flex-1 flex items-center justify-center text-muted-foreground"
    >
      Loading conversation...
    </div>

    <template v-else>
      <!-- Messages area -->
      <div
        ref="messagesContainer"
        class="flex-1 overflow-y-auto scrollbar-thin"
      >
        <div class="max-w-3xl mx-auto w-full px-6 py-6 space-y-6">
          <div
            v-if="messages.length === 0 && !isStreaming"
            class="text-center text-muted-foreground py-16"
          >
            Tell me what you'd like to learn and I'll design a curriculum for you.
          </div>

          <div
            v-for="(msg, i) in messages"
            :key="i"
            class="flex"
            :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
          >
            <div
              :class="
                msg.role === 'user'
                  ? 'max-w-[80%] rounded-2xl px-4 py-2 bg-primary text-primary-foreground'
                  : 'w-full text-foreground'
              "
            >
              <div
                v-if="msg.role === 'assistant'"
                class="prose dark:prose-invert prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-hr:my-4 prose-headings:my-3"
                v-html="marked.parse(normalizeMarkdown(msg.content.trim()))"
              />
              <div
                v-else
                class="whitespace-pre-wrap"
              >
                {{ msg.content.trim() }}
              </div>
              <span
                v-if="msg.isStreaming && !msg.content"
                class="inline-flex gap-1"
              >
                <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.3s]" />
                <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce [animation-delay:-0.15s]" />
                <span class="w-1.5 h-1.5 rounded-full bg-current animate-bounce" />
              </span>
              <span
                v-else-if="msg.isStreaming"
                class="inline-block w-0.5 h-4 bg-current animate-pulse ml-0.5 align-text-bottom"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom dock: success / error / input -->
      <div class="shrink-0 px-6 pb-4">
        <div class="max-w-3xl mx-auto w-full">
          <!-- Success banner -->
          <div
            v-if="createdCurriculumId"
            class="bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-700 p-4 rounded-lg mb-3 flex items-center justify-between"
          >
            <span class="text-green-800 dark:text-green-200">Curriculum created!</span>
            <Button
              size="sm"
              @click="router.push(`/learn/${createdCurriculumId}`)"
            >
              View Curriculum
            </Button>
          </div>

          <!-- Error display -->
          <div
            v-if="error"
            class="bg-destructive/10 text-destructive p-3 rounded-md mb-3 text-sm"
          >
            {{ error }}
          </div>

          <!-- Input area -->
          <form
            class="flex items-end gap-2"
            @submit.prevent="handleSubmit"
          >
            <textarea
              ref="textareaRef"
              v-model="inputText"
              :disabled="isStreaming || !!createdCurriculumId"
              rows="1"
              class="flex-1 px-3 py-2 border border-input rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 resize-none max-h-40 overflow-y-auto scrollbar-thin"
              placeholder="Type your message..."
              @keydown.enter.exact.prevent="handleSubmit"
              @input="autoResize"
            />
            <Button
              type="submit"
              :disabled="isStreaming || !inputText.trim() || !!createdCurriculumId"
            >
              Send
            </Button>
          </form>
        </div>
      </div>
    </template>
  </AppLayout>
</template>
